import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { gemLabel } from "@/lib/gems";
import { GemButtons } from "@/components/BirthstoneFinder";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { api, money } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { PageHeader, Spinner } from "@/components/common";
import { ShapeIcon } from "@/components/ShapeIcon";
import { useMeta } from "@/hooks/useMeta";
import { NotFound } from "@/pages/Content";
import { HeroRotator } from "@/components/HeroRotator";
import { useSite } from "@/context/SiteContext";
import { TypeStrip } from "@/components/TypeStrip";
import { WaitlistForm } from "@/pages/StylePage";

function CollectionHeader({ slug, col, eyebrow, title, hideIntro = false, introText, heroSlides }: { slug?: string; col: any; eyebrow: string; title: string; hideIntro?: boolean; introText?: string; heroSlides?: any[] }) {
  const intro = introText || col?.intro;
  const { site } = useSite() as any;
  const base = slug && site?.menu_heroes?.[slug];
  const hero = useMemo(() => (heroSlides?.length ? { interval: 4, ...base, rotation: "random", slides: heroSlides } : base), [base, heroSlides]);
  const block = useMemo(() => (hero ? { ...hero, heading: title, eyebrow: col?.title } : null), [hero, title, col?.title]);
  if (!hero || !block || !(hero.slides || []).some((x: any) => x.enabled !== false && x.image)) return <PageHeader eyebrow={eyebrow} title={title} intro={hideIntro ? "" : intro} />;
  return (
    <>
      <HeroRotator banner b={block} />
      {intro && !hideIntro ? <p className="max-w-2xl mx-auto text-center text-sm md:text-base text-neutral-600 px-5 pt-10" data-testid="collection-intro">{intro}</p> : null}
      <div className="h-10" />
    </>
  );
}

const SORTS = [["featured", "Featured"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["newest", "Newest"], ["name", "Name"]];
const PRICES = [[0, 2000], [2000, 4000], [4000, 7000], [7000, 100000]];

function FacetGroup({ title, name, values, params, toggle, icon = false }: any) {
  if (!values?.length) return null;
  const sel = (params.get(name) || "").split(",").filter(Boolean);
  return (
    <div className="py-6 border-b border-neutral-200">
      <p className="eyebrow mb-4">{title}</p>
      <div className={icon ? "grid grid-cols-3 gap-2" : "space-y-2.5"}>
        {values.map((v: any) => {
          const on = sel.includes(v.value);
          return icon ? (
            <button key={v.value} onClick={() => toggle(name, v.value)} data-testid={`filter-${name}-${v.value}`}
              className={`flex flex-col items-center gap-1 py-3 border text-[10px] transition-colors ${on ? "border-neutral-900 bg-white" : "border-transparent hover:border-neutral-300"}`}>
              <ShapeIcon shape={v.value} size={28} />{v.value}
            </button>
          ) : (
            <label key={v.value} className="flex items-center gap-3 text-sm cursor-pointer hover:text-[var(--c-primary)]" data-testid={`filter-${name}-${v.value}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(name, v.value)} className="accent-neutral-900 h-4 w-4" />
              <span className="flex-1">{v.value}</span><span className="text-xs text-neutral-400">{v.count}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function Filters({ facets, params, toggle, setPrice, styleParam }: any) {
  const cur = `${params.get("min_price") || ""}-${params.get("max_price") || ""}`;
  return (
    <div data-testid="filter-panel">
      <FacetGroup title="Shape" name="shape" values={facets?.shapes?.filter((s: any) => s.value.length < 12)} params={params} toggle={toggle} icon />
      {styleParam === "ring_style" && <FacetGroup title="Ring style" name="ring_style" values={facets?.ring_styles} params={params} toggle={toggle} />}
      {styleParam === "band_style" && <FacetGroup title="Band style" name="band_style" values={facets?.band_styles} params={params} toggle={toggle} />}
      <FacetGroup title="Metal" name="metal" values={facets?.metals} params={params} toggle={toggle} />
      <FacetGroup title="Style" name="style" values={facets?.styles} params={params} toggle={toggle} />
      <div className="py-6 border-b border-neutral-200">
        <p className="eyebrow mb-4">Price</p>
        <div className="space-y-2.5">
          {PRICES.map(([a, b]) => (
            <label key={a} className="flex items-center gap-3 text-sm cursor-pointer" data-testid={`filter-price-${a}`}>
              <input type="radio" name="price" checked={cur === `${a}-${b}`} onChange={() => setPrice(a, b)} className="accent-neutral-900" />
              {b >= 100000 ? `${money(a)}+` : `${money(a)} – ${money(b)}`}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function StyleSoon({ tiles, params }: any) {
  const sel = tiles ? (params.get(tiles.param) || "").split(",").filter(Boolean) : [];
  const t = sel.length === 1 && tiles.tiles.find((x: any) => x.value === sel[0] && !x.count);
  if (!t) return <p className="py-24 text-center font-display text-2xl text-neutral-500" data-testid="no-results">No pieces match your selection.</p>;
  return (
    <div className="py-20 text-center max-w-lg mx-auto" data-testid="style-coming-soon">
      <p className="eyebrow text-[var(--c-primary)]">Coming soon</p>
      <p className="font-display text-2xl md:text-3xl font-light mt-4">{t.label} pieces are on their way.</p>
      {t.explainer && <p className="text-sm text-neutral-600 mt-4">{t.explainer}</p>}
      <WaitlistForm page={tiles.page} style={t.value} label={t.label} />
      <Link to="/consultation" className="text-xs underline inline-block mt-6" data-testid="style-coming-soon-cta">Or design one with us</Link>
    </div>
  );
}

function ComingSoon({ col }: any) {
  return (
    <div className="max-w-xl mx-auto px-5 py-20 md:py-28 text-center rise" data-testid="coming-soon">
      <p className="eyebrow text-[var(--c-primary)]">Coming soon</p>
      <p className="font-display text-2xl md:text-3xl font-light leading-snug mt-5" data-testid="coming-soon-text">{col.coming_soon || "New pieces are being crafted for this collection. Check back soon."}</p>
      <Link to={col.coming_soon_cta_url || "/collections/rings"} className="btn-ink inline-block mt-10 px-9 py-3.5 text-xs tracking-widest uppercase" data-testid="coming-soon-cta">
        {col.coming_soon_cta_label || "Shop all rings"}
      </Link>
    </div>
  );
}

export default function Collection() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [col, setCol] = useState<any>(null);
  const [data, setData] = useState<any>(null);
  const [missing, setMissing] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const q = params.get("q");
  const gem = params.get("gem");

  const [tiles, setTiles] = useState<any>(null);
  useEffect(() => { setTiles(null); if (slug) api.get(`/style-tiles/${slug}`).then((r) => setTiles(r.data)).catch(() => {}); }, [slug]);

  useEffect(() => {
    setCol(null); setMissing(false);
    if (slug) api.get(`/collections/${slug}`).then((r) => setCol(r.data)).catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => {
    const p: any = Object.fromEntries(params.entries());
    if (slug) p.collection = slug;
    let live = true;
    api.get("/products", { params: { limit: 24, ...p } }).then((r) => live && setData(r.data)).catch(() => live && setMissing(true));
    return () => { live = false; };
  }, [slug, params]);

  useMeta(col ? col.seo_title || col.title : gem ? `Lab-Grown ${gemLabel(gem)} Jewelry` : q ? `Search: ${q}` : slug ? undefined : "Search", col?.seo_description || col?.intro, null, { image: col?.image, noindex: !!col?.noindex });

  const update = (fn: (p: URLSearchParams) => void) => { const n = new URLSearchParams(params); fn(n); n.delete("page"); setParams(n); };
  const toggle = (name: string, value: string) => update((n) => {
    const s = new Set((n.get(name) || "").split(",").filter(Boolean));
    s.has(value) ? s.delete(value) : s.add(value);
    s.size ? n.set(name, [...s].join(",")) : n.delete(name);
  });
  const setPrice = (a: number, b: number) => update((n) => { n.set("min_price", String(a)); n.set("max_price", String(b)); });
  const active = ["ring_style", "band_style", "shape", "metal", "style"].flatMap((k) => (params.get(k) || "").split(",").filter(Boolean).map((v) => [k, v]));
  const page = Number(params.get("page") || 1);
  const soon = col && data && data.total === 0 && !active.length && !params.get("min_price") && !q;

  if (missing && slug) return <NotFound />;
  if (soon) return (
    <div data-testid="collection-page">
      <CollectionHeader slug={slug} col={col} eyebrow="Collection" title={col.title} />
      <ComingSoon col={col} />
    </div>
  );
  return (
    <div data-testid="collection-page">
      <CollectionHeader slug={slug} col={col} eyebrow={slug ? "Collection" : "Search"} title={col?.title || (gem ? `Lab-Grown ${gemLabel(gem)}` : q ? `“${q}”` : slug ? "" : "All Jewelry")} hideIntro={tiles?.show_intro === false || tiles?.intro_position === "below"} introText={tiles?.intro_active} heroSlides={tiles?.hero_active} />
      {tiles && data?.facets && <TypeStrip config={tiles} facets={data.facets} params={params} toggle={toggle}
        clear={() => update((n) => { n.delete(tiles.param); n.delete("shape"); })} />}
      {tiles?.intro_position === "below" && (tiles.intro_active || col?.intro) && <p className="max-w-2xl mx-auto text-center text-sm md:text-base text-neutral-600 px-5 -mt-4 mb-12" data-testid="collection-intro">{tiles.intro_active || col.intro}</p>}
      <div className="max-w-[1400px] mx-auto px-5 md:px-12 grid lg:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden lg:block"><Filters facets={data?.facets} params={params} toggle={toggle} setPrice={setPrice} styleParam={tiles?.param} /></aside>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-neutral-200 mb-10">
            <div className="flex items-center gap-3 flex-wrap">
              <button className="lg:hidden flex items-center gap-2 eyebrow border border-neutral-300 px-4 py-2.5" onClick={() => setMobileOpen(true)} data-testid="mobile-filter-button"><SlidersHorizontal size={14} /> Filter</button>
              <span className="text-sm text-neutral-500" data-testid="result-count">{data ? `${data.total} pieces` : ""}</span>
              {active.map(([k, v]) => (
                <button key={k + v} onClick={() => toggle(k, v)} className="flex items-center gap-1.5 text-xs bg-white border border-neutral-200 px-3 py-1.5 hover:border-neutral-900" data-testid={`active-filter-${v}`}>{v}<X size={12} /></button>
              ))}
              {(active.length > 0 || params.get("min_price")) && <button onClick={() => setParams({ ...(q ? { q } : {}), ...(gem ? { gem } : {}) })} className="text-xs underline" data-testid="clear-filters">Clear all</button>}
            </div>
            <select value={params.get("sort") || "featured"} onChange={(e) => update((n) => n.set("sort", e.target.value))} className="bg-transparent border border-neutral-300 px-3 py-2.5 text-sm" data-testid="sort-select">
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {!data ? <Spinner /> : data.items.length === 0 ? (gem && !active.length ? (
            <div className="py-20 text-center" data-testid="gem-empty"><p className="font-display text-2xl text-neutral-500 mb-6">No {gemLabel(gem).toLowerCase()} pieces are in the shop right now — we'd love to design one for you.</p><GemButtons gem={gem} center hideShop tid="gem-empty" /></div>
          ) : (
            <StyleSoon tiles={tiles} params={params} />
          )) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-14 md:gap-x-8" data-testid="product-grid">
              {data.items.map((p: any, i: number) => <ProductCard key={p.id} p={p} index={i} />)}
            </div>
          )}
          {data?.pages > 1 && (
            <div className="flex justify-center gap-2 mt-16" data-testid="pagination">
              {Array.from({ length: data.pages }, (_, i) => i + 1).map((n) => (
                <button key={n} onClick={() => { const x = new URLSearchParams(params); x.set("page", String(n)); setParams(x); window.scrollTo(0, 0); }}
                  className={`h-10 w-10 text-sm border ${n === page ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`} data-testid={`page-${n}`}>{n}</button>
              ))}
            </div>
          )}
        </div>
      </div>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="bg-[var(--c-bg)] overflow-y-auto w-[88vw]">
          <SheetTitle className="font-display text-2xl">Filter</SheetTitle>
          <Filters facets={data?.facets} params={params} toggle={toggle} setPrice={setPrice} styleParam={tiles?.param} />
          <button onClick={() => setMobileOpen(false)} className="btn-ink w-full py-3.5 mt-6 text-xs tracking-widest uppercase" data-testid="mobile-filter-apply">Show {data?.total} pieces</button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
